import * as React from 'react';

import { ItemHierarchyElement } from '@/dialogs/item-editor/ItemEditor.queries';

interface HierarchyNode {
    itemPrimaryKey: string;
    level: number;
    isRoot: boolean;
    hasChildren: boolean;
}

/**
 * Custom hook to build hierarchy tree from base to children
 * The new singleBaseHierarchyElements endpoint returns a complete hierarchy
 * starting from the base unit (level 0) and showing all its children
 */
export const useHierarchyTree = (hierarchiesData: ItemHierarchyElement[] | undefined) => {
    const buildHierarchyTree = React.useCallback((elements: ItemHierarchyElement[]): HierarchyNode[] => {
        if (!elements || elements.length === 0) return [];

        const result: HierarchyNode[] = [];

        // Simply map the elements in the order they come from the API
        elements.forEach((element) => {
            result.push({
                itemPrimaryKey: element.primaryKey,
                level: element.level || 0,
                isRoot: element.root,
                hasChildren: Boolean(element.childPrimaryKeys?.length)
            });
        });

        return result;
    }, []);

    return buildHierarchyTree(hierarchiesData || []);
};

/**
 * Custom hook to check if current item has parents
 */
export const useHasParents = (hierarchiesData: ItemHierarchyElement[] | undefined, currentItemKey: string | undefined) => {
    return React.useMemo(() => {
        if (!hierarchiesData || !currentItemKey) return false;

        const currentItem = hierarchiesData?.find(element => element.primaryKey === currentItemKey);
        return Boolean(currentItem?.parentPrimaryKeys?.length);
    }, [hierarchiesData, currentItemKey]);
};

export type { HierarchyNode };
